/**
 * Tasks API - Frontend utilities for cross-device task sync
 */

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:3100';

/**
 * Get all tasks from the server
 * @param {string} token - Auth token
 * @returns {Promise<Object>} { tasks: [...], updatedAt: string }
 */
export async function getTasks(token) {
  const response = await fetch(`${API_BASE}/api/tasks`, {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${token}`
    }
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Failed to get tasks' }));
    throw new Error(error.error || 'Failed to get tasks');
  }

  return response.json();
}

/**
 * Sync all tasks to the server (full replace)
 * @param {string} token - Auth token
 * @param {Array} tasks - All tasks
 * @param {string} clientUpdatedAt - Last known update time from client
 * @returns {Promise<Object>} { success, updatedAt, hadConflict }
 */
export async function syncTasks(token, tasks, clientUpdatedAt = null) {
  const response = await fetch(`${API_BASE}/api/tasks`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    },
    body: JSON.stringify({
      tasks,
      clientUpdatedAt
    })
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Failed to sync tasks' }));
    throw new Error(error.error || 'Failed to sync tasks');
  }

  return response.json();
}

/**
 * Add a single task to the server
 * @param {string} token - Auth token
 * @param {Object} task - Task to add
 * @returns {Promise<Object>} { success, task, updatedAt }
 */
export async function addTask(token, task) {
  const response = await fetch(`${API_BASE}/api/tasks`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    },
    body: JSON.stringify({ task })
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Failed to add task' }));
    throw new Error(error.error || 'Failed to add task');
  }

  return response.json();
}

/**
 * Update a single task on the server
 * @param {string} token - Auth token
 * @param {number} taskId - Task ID
 * @param {Object} updates - Fields to update
 * @returns {Promise<Object>} { success, task, updatedAt }
 */
export async function updateTask(token, taskId, updates) {
  const response = await fetch(`${API_BASE}/api/tasks/${taskId}`, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    },
    body: JSON.stringify({ updates })
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Failed to update task' }));
    throw new Error(error.error || 'Failed to update task');
  }

  return response.json();
}

/**
 * Delete a task from the server
 * @param {string} token - Auth token
 * @param {number} taskId - Task ID to delete
 * @returns {Promise<Object>} { success, taskId, updatedAt }
 */
export async function deleteTask(token, taskId) {
  const response = await fetch(`${API_BASE}/api/tasks/${taskId}`, {
    method: 'DELETE',
    headers: {
      'Authorization': `Bearer ${token}`
    }
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Failed to delete task' }));
    throw new Error(error.error || 'Failed to delete task');
  }

  return response.json();
}
